'use client'

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useChat } from '@ai-sdk/react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bot,
  X,
  History,
  Plus,
  ArrowDown
} from 'lucide-react'
import { VirtuosoHandle } from 'react-virtuoso'
import { Card, Button } from '@sarkiai/ui'
import { createIdGenerator, DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai'
import { useTranslations, useLanguage } from '@/components/I18nProvider'
import { useChatHistory } from '@/hooks/useChatHistory'
import { useAssistant } from './AssistantContext'
import { AssistantMessageList } from './AssistantMessageList'
import { AssistantLockScreen } from './AssistantLockScreen'
import { ChatInput } from './ChatInput'

// Custom sendAutomaticallyWhen that respects suppressFollowUp in tool outputs
const customSendAutomaticallyWhen = ({ messages }: { messages: any[] }) => {
  const isComplete = lastAssistantMessageIsCompleteWithToolCalls({ messages })
  if (!isComplete) return false

  const lastMessage = messages[messages.length - 1]
  if (lastMessage?.role === 'assistant' && lastMessage.parts) {
    for (const part of lastMessage.parts) {
      if (part.type?.startsWith('tool-')) {
        const result = part.output || part.result
        if (result?.suppressFollowUp === true) return false
      }
    }
  }
  return true
}

export function FloatingAssistant() {
  const { isPending } = useAssistant()
  if (isPending) return null
  return <FloatingAssistantContent />
}

function FloatingAssistantContent() {
  const {
    isOpen,
    isMinimized,
    openAssistant,
    closeAssistant,
    minimizeAssistant,
    toggleAssistant,
    isAnonymous,
    isPending,
    isLocked,
    quota,
    refreshQuota,
    setLoadHandler,
    pendingMessage,
    setPendingMessage,
    view,
    setView,
    isQuotaLockForced,
    setIsQuotaLockForced,
    setLockDismissed,
    lockDismissed,
  } = useAssistant()

  const { sessions, saveSession, deleteSession } = useChatHistory()

  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const previousStatusRef = useRef<string>('')
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [hasBeenFocused, setHasBeenFocused] = useState(false)
  const [input, setInput] = useState('')

  const { locale: lang } = useLanguage()
  const t = useTranslations('chat')

  // Stable ID generator — memoized so it never changes between renders (xedu pattern)
  const generateId = useMemo(() => createIdGenerator({ size: 16, prefix: 'dj' }), [])

  const {
    id,
    stop,
    status,
    messages,
    setMessages,
    sendMessage,
  } = useChat({
    // @ts-ignore — experimental_throttle is valid
    experimental_throttle: 50,
    generateId: generateId,
    sendAutomaticallyWhen: customSendAutomaticallyWhen,
    transport: new DefaultChatTransport({
      api: isAnonymous ? '/api/v1/chat/anonymus' : '/api/v1/chat',
      prepareSendMessagesRequest({ messages, id }) {
        return {
          body: {
            id,
            lang,
            message: messages[messages.length - 1],
          }
        }
      },
    }),

    async onToolCall({ toolCall }) {
      if (toolCall.dynamic) return
    },

    onFinish: () => {
      // Save session metadata to localStorage (xedu pattern)
      if (messages.length > 0) {
        saveSession(id, [messages[0]])
      }

      if (isAnonymous) {
        refreshQuota()
      }

      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({ index: 999999, align: 'end' })
      }, 100)
    },

    onError: (error) => {
      console.error("Chat error:", error)
    }
  })

  // Handle auto-sending pending messages from context
  useEffect(() => {
    if (pendingMessage && isOpen && status !== 'streaming' && status !== 'submitted') {
      const msg = pendingMessage
      setPendingMessage(null)
      sendMessage({ role: 'user', parts: [{ type: 'text', text: msg }] })

      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({ index: 999999, align: 'end' })
      }, 100)
    }
  }, [pendingMessage, isOpen, status, sendMessage, setPendingMessage])

  // Register loading callback in context
  useEffect(() => {
    setLoadHandler(() => (historicalMessages: any[]) => {
      if (Array.isArray(historicalMessages)) {
        setMessages(historicalMessages)
        setHasBeenFocused(true)
      }
    })
  }, [setLoadHandler, setMessages])

  // Lock body scroll when assistant is open
  useEffect(() => {
    if (isOpen && !isMinimized) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [isOpen, isMinimized])

  // Focus textarea when streaming finishes
  useEffect(() => {
    if (previousStatusRef.current === 'streaming' && status !== 'streaming' && status !== 'submitted') {
      if (window.innerWidth >= 1024 && !isLocked) {
        setTimeout(() => {
          textareaRef.current?.focus()
        }, 250)
      }
    }
    previousStatusRef.current = status
  }, [status, isLocked])

  const isStreaming = status === 'streaming' || status === 'submitted'

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setInput(e.target.value)
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!input.trim()) return

    if (isLocked && !lockDismissed) {
      setLockDismissed(false)
      setIsQuotaLockForced(true)
      return
    }

    const isBypassing = isLocked && lockDismissed
    const value = input
    setInput('')

    // Reset lockDismissed BEFORE sending so only ONE bypass message is allowed
    if (isBypassing) {
      setLockDismissed(false)
    }

    await sendMessage({ role: 'user', parts: [{ type: 'text', text: value }] }, {
      headers: isBypassing ? { 'X-Djotta-Bypass-Quota': 'true' } : {}
    })

    // Refresh quota after bypass to get the latest state
    if (isBypassing && isAnonymous) {
      refreshQuota()
    }

    setTimeout(() => {
      virtuosoRef.current?.scrollToIndex({
        align: 'end',
        behavior: 'smooth',
        index: messages.length - 1
      })
    }, 250)
  }

  const handleSuggestedPrompt = (prompt: string) => {
    if (isLocked) return
    setHasBeenFocused(true)
    setInput('')

    sendMessage({ role: 'user', parts: [{ type: 'text', text: prompt }] })

    setTimeout(() => {
      virtuosoRef.current?.scrollToIndex({ index: messages.length - 1, align: 'end', behavior: 'smooth' })
    }, 250)
  }

  const handleNewChat = useCallback(() => {
    setMessages([])
    setView('chat')
    setHasBeenFocused(false)
    setLockDismissed(false)
    setIsQuotaLockForced(false)
  }, [setMessages, setView, setLockDismissed, setIsQuotaLockForced])

  const handleClose = useCallback(() => {
    setHasBeenFocused(false)
    setMessages([])
    closeAssistant()
  }, [setMessages, closeAssistant])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // @ts-expect-error: virtuoso event type mismatch
    const { scrollTop, scrollHeight, clientHeight } = e.target
    const atBottom = scrollHeight - scrollTop - clientHeight <= 50
    setIsAtBottom(atBottom)
    setShowScrollButton(!atBottom)
  }

  const scrollToBottom = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({ index: messages.length - 1, align: 'end' })
  }, [messages.length])

  const isPanelExpanded = isOpen && !isMinimized && (messages.length >= 1 || view === 'history' || hasBeenFocused)

  return (
    <>
      {/* Panel & Backdrop */}
      <AnimatePresence>
        {isPanelExpanded && (
          <>
            <motion.div
              key="assistant-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              exit={{ opacity: 0, pointerEvents: 'none' }}
              className="fixed inset-0 z-[89] touch-none overscroll-none bg-black/5"
              onClick={minimizeAssistant}
            />
            <motion.div
              key="assistant-panel"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              exit={{ opacity: 0, scale: 0.95, y: 20, pointerEvents: 'none' }}
              className="fixed top-2 sm:top-4 left-0 right-0 bottom-20 z-[90] flex flex-col items-center px-2 sm:px-4"
            >
              <div className="w-full max-w-3xl h-full flex flex-col">
                <AssistantMessageList
                  messages={messages}
                  status={status}
                  sessions={sessions}
                  virtuosoRef={virtuosoRef}
                  onScroll={handleScroll}
                  onAtBottomChange={setIsAtBottom}
                  onDeleteSession={deleteSession}
                  onSuggestedPrompt={(p) => {
                    setView('chat')
                    handleSuggestedPrompt(p)
                  }}
                  onNewChat={handleNewChat}
                  onClose={minimizeAssistant}
                  isAnonymous={isAnonymous}
                  isLocked={isLocked}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Floating Button / Avatar Bot */}
      <AnimatePresence>
        {isMinimized && (
          <motion.div
            initial={{ opacity: 0, scale: 0, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0, y: 20 }}
            className="fixed bottom-0 left-1/2 -translate-x-1/2 z-[100]"
          >
            <Button
              onClick={openAssistant}
              className="h-12 w-16 rounded-t-[5rem] rounded-b-none bg-primary hover:bg-primary/90 shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-105"
            >
              <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-6 w-6 text-white" />
              </div>
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Prompt Input Area */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ opacity: 0, scale: 0.3, y: 150 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              transition: { type: "spring", stiffness: 400, damping: 25, mass: 0.5 }
            }}
            exit={{
              opacity: 0,
              scale: 0.3,
              y: 150,
              transition: { duration: 0.2, ease: "easeIn" }
            }}
            className="fixed bottom-0 left-0 right-0 p-2 sm:p-4 z-[100] safe-area-bottom"
          >
            <div className="w-full max-w-xl mx-auto relative pointer-events-auto">
              {/* {(!hasBeenFocused || isPanelExpanded) && (
                <div className="hidden md:flex items-center justify-end absolute -top-10 right-2 sm:top-2.5 sm:-right-10">
                  <Button
                    size="icon"
                    variant="default"
                    onClick={minimizeAssistant}
                    className="h-8 w-8 rounded-full bg-primary hover:bg-primary/80"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )} */}
              <ChatInput
                ref={textareaRef}
                input={input}
                status={status}
                stop={stop}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                onClose={minimizeAssistant}
                onFocus={() => {
                  setHasBeenFocused(true)
                  if (!isOpen) openAssistant()
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
